import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { usePropertyContext } from '../../../contexts/propertyProvider';
import { useUserDataContext } from '../../../contexts/userDataProvider';
import { addOneDayToDate, getObjectByValue, initOrderData } from '../../common/utils';
import { defaultRangeKey } from '../../common/appSettings';
import { fitTimeInDate } from '../../common/addNewOrdersUtils';
import { useCreate } from '../../common/requests.hooks';
import ordersAPI from '../../../services/ordersAPI';
import Loader from '../../common/Loader';
import ErrorAlert from '../../common/ErrorAlert';
import OrderForm from './OrderForm';

const AddOrder = ({
  clickedItemData,
  closeEditModalReloadData,
  allOrders,
  timeLineLoadedDataRange
}) => {
  const { translate } = useUserDataContext();

  const [dataRange, setDataRange] = useState({});
  const [orderData, setOrderData] = useState({});

  const {
    data: propertyData,
    isLoading: propertyIsLoading,
    error: propertyError
  } = usePropertyContext();

  const { isLoading: creating, error: createError, create } = useCreate(
    ordersAPI.saveOrder,
    {
      onSuccess: () => {
        setOrderData(initOrderData);
        closeEditModalReloadData();
      }
    }
  );

  // Set Order data by form elements
  const handleFormData = (formData) => {
    const key = Object.keys(formData)[0];

    setOrderData(prevState => ({
      ...prevState,
      [key]: formData[key]
    }));
  };

  const payload = {
    ...orderData,
    dateFrom: dataRange.startDate,
    dateTo: dataRange.endDate,
    costPerNight: orderData.costPerNight
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    create(payload);
  };

  // Init data
  useEffect(() => {
    const handleTimeLineNewOrder = () => {
      const selectedTimeLineProperty = getObjectByValue(propertyData, 'title', clickedItemData.propertyName);
      const endDate = addOneDayToDate(clickedItemData.clickedDate);

      // Set clicked date to calculate availabele dates
      setDataRange(
        {
          startDate: fitTimeInDate(clickedItemData.clickedDate),
          endDate: fitTimeInDate(endDate),
          key: defaultRangeKey // TODO: check if needed 
        }
      );
      setOrderData({
        // Reset form data if it was filled before and not submitted
        ...initOrderData,
        // Init property data to calculate available date and highlight selected property
        propertyId: selectedTimeLineProperty?.id,
        propertyName: clickedItemData.propertyName,
        costPerNight: selectedTimeLineProperty?.costPerNight
      });
    };

    // Avoid wrong init - when Group edited, clickedItemData.propertyName is undefined
    if(!clickedItemData.propertyName || !clickedItemData.clickedDate || !propertyData?.length) {
      return;
    }

    handleTimeLineNewOrder();
  }, [clickedItemData, propertyData]);

  return (
    <div className="order-view-wrap">
      <Loader 
        setIsLoading={creating || propertyIsLoading}
      />
      <ErrorAlert
        error={createError || propertyError}
      />
      <OrderForm
        translate={translate}
        allOrders={allOrders}
        timeLineLoadedDataRange={timeLineLoadedDataRange}
        dataRange={dataRange}
        setDataRange={setDataRange}
        orderData={orderData}
        isOrderEdit={false}
        setOrderData={setOrderData} // On Property click - to get reserved data for DateRangePicker
        handleFormData={handleFormData}
        handleSubmit={handleSubmit}
      />
    </div>
  );
};

AddOrder.propTypes = {
  clickedItemData: PropTypes.shape({
    isTimeLineNewOrder: PropTypes.bool,
    isTimeLineEditOrder: PropTypes.bool,
    orderId: PropTypes.string,
    propertyName: PropTypes.string,
    clickedDate: PropTypes.instanceOf(Date)
  }),
  closeEditModalReloadData: PropTypes.func.isRequired,
  allOrders: PropTypes.arrayOf(
    PropTypes.shape({})
  ).isRequired,
  timeLineLoadedDataRange: PropTypes.shape({
    dateFrom: PropTypes.instanceOf(Date),
    dateTo: PropTypes.instanceOf(Date)
  }).isRequired
};

export default AddOrder;
